import React, { useState } from 'react'
import { Form, Input, Table } from 'antd'
import { ColumnsType } from 'antd/lib/table'
import { useRequest } from 'ahooks'
import Button from '@/components/button/button'
import { Row } from '@/components/layout/layout'
import { IMemberGroupListItem } from '@/model/types/member'
import $msg from '@/utils/message'
import { Modal as SgModal } from '@/components/dialog/modal'
import { layerManage, showLayer } from '@/utils/layer'
import Auth from '@/views/main/components/auth/auth'
import { editMemberGroup, getMemberGroupList } from '../api'
import SettingPage, { SettingTableContainer } from '../member-setting/setting-page'
import { useMemberSettingAccess } from '../member-setting/setting-access'
import { shopStore } from '@/store/shop'

interface GroupForm {
    name: string
    description?: string
}

const MemberGroup: React.FC = () => {
    const [form] = Form.useForm<GroupForm>()
    const [visible, setVisible] = useState(false)
    const [current, setCurrent] = useState<IMemberGroupListItem | null>(null)
    const [saving, setSaving] = useState(false)
    const access = useMemberSettingAccess()
    const listReq = useRequest(getMemberGroupList, {
        onError: e => $msg('error', e.message || '获取会员分组失败'),
    })

    const openEditor = (group?: IMemberGroupListItem) => {
        if (!group) {
            const limit = [1, 5, 10][shopStore.currentVersion]
            if (limit && (listReq.data || []).length >= limit) {
                $msg('warning', `当前版本最多可创建 ${limit} 个会员分组，如需更多分组请升级版本`)

                return
            }
        }
        setCurrent(group || null)
        form.setFieldsValue({ name: group?.name || '', description: group?.description || '' })
        setVisible(true)
    }

    const onSubmit = async () => {
        try {
            const values = await form.validateFields()
            setSaving(true)
            await editMemberGroup({
                group_id: current?.id,
                name: values.name,
                description: values.description,
            })
            $msg('success', current ? '编辑成功' : '新建成功')
            setVisible(false)
            listReq.refresh()
        } catch (e) {
            if (e && e.errorFields) return
            $msg('error', e.message || '保存分组失败')
        } finally {
            setSaving(false)
        }
    }

    const onDelete = (group: IMemberGroupListItem) => {
        showLayer(
            <SgModal
                headerStyles={{ width: '440px' }}
                isShow
                type="danger"
                style={{ height: '156px' }}
                onClose={() => layerManage.pop()}
                onCancel={() => layerManage.pop()}
                onConfirm={() => {
                    layerManage.pop()
                    editMemberGroup({
                        group_id: group.id,
                        name: group.name,
                        description: group.description,
                        status: -1,
                    })
                        .then(() => {
                            $msg('success', '删除成功')
                            listReq.refresh()
                        })
                        .catch(e => $msg('error', e.message || '删除分组失败'))
                }}
            >
                <div>
                    <div data-font-size-20 data-font-weight-600 data-color-000>
                        确认删除分组
                        <span data-color-ff4343 data-margin-h-6>
                            {group.name}
                        </span>
                        吗？
                    </div>
                    <div data-font-size-14 data-color-999 data-margin-top-8>
                        该分组下会员会回到默认分组
                    </div>
                </div>
            </SgModal>,
        )
    }

    const columns: ColumnsType<IMemberGroupListItem> = [
        { title: '分组名称', dataIndex: 'name', width: 180 },
        { title: '说明', dataIndex: 'description', render: value => value || '-' },
        { title: '会员数', dataIndex: 'member_count', width: 110 },
        {
            title: '操作',
            width: 140,
            render: (_value, record) =>
                access.readOnly ? (
                    '/'
                ) : (
                    <Row>
                        <Auth perssion={19} onClick={() => openEditor(record)}>
                            <span className="action">编辑</span>
                        </Auth>
                        {!record.is_default && (
                            <Auth perssion={19} onClick={() => onDelete(record)}>
                                <span className="action danger">删除</span>
                            </Auth>
                        )}
                    </Row>
                ),
        },
    ]

    return (
        <SettingPage
            title="分组管理"
            readOnly={access.readOnly}
            className="member-basic-setting"
            bodyClassName="member-basic-setting__body"
            extra={
                access.readOnly ? undefined : (
                    <Auth perssion={19} onClick={() => openEditor()}>
                        <Button types="default">新建分组</Button>
                    </Auth>
                )
            }
            append={
                visible ? (
                    <SgModal
                        title={current ? '编辑分组' : '新建分组'}
                        isShow
                        type="success"
                        loading={saving}
                        onConfirm={onSubmit}
                        onClose={() => setVisible(false)}
                        onCancel={() => setVisible(false)}
                        okText="确认"
                        cancelText="取消"
                    >
                        <Form form={form} layout="vertical">
                            <Form.Item
                                name="name"
                                label="分组名称"
                                rules={[{ required: true, message: '请输入分组名称' }]}
                            >
                                <Input maxLength={20} placeholder="请输入分组名称" />
                            </Form.Item>
                            <Form.Item name="description" label="说明">
                                <Input.TextArea rows={3} maxLength={100} placeholder="请输入说明" />
                            </Form.Item>
                        </Form>
                    </SgModal>
                ) : null
            }
        >
            <SettingTableContainer className="member-basic-setting__panel">
                <Table
                    bordered
                    rowKey="id"
                    loading={listReq.loading}
                    columns={columns}
                    dataSource={listReq.data || []}
                    pagination={false}
                />
            </SettingTableContainer>
        </SettingPage>
    )
}

export default MemberGroup
